import { useMediaDeviceSelect } from "@livekit/components-react";
import { Mic, Settings, Video, Volume2 } from "lucide-react";
import { useEffect } from "react";
import { useStore } from "zustand";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { PopoverContent } from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { uiStore } from "@/lib/stores/ui-store";
import { VoiceVideoSettings } from "./voice-video-settings";

const DEVICE_ID_PREVIEW_LENGTH = 8;

type MediaDeviceSelectProps = {
	kind: MediaDeviceKind;
	placeholder?: string;
	icon?: React.ReactNode;
};

function CompactMediaDeviceSelect({
	kind,
	placeholder,
	icon,
}: MediaDeviceSelectProps) {
	const { devices, activeDeviceId, setActiveMediaDevice } =
		useMediaDeviceSelect({ kind });
	const deviceSelections = useStore(uiStore, (state) => state.deviceSelections);
	const setDeviceSelection = useStore(
		uiStore,
		(state) => state.setDeviceSelection
	);

	useEffect(() => {
		const savedDeviceId = deviceSelections[kind];
		if (
			savedDeviceId &&
			devices.some((d) => d.deviceId === savedDeviceId) &&
			activeDeviceId !== savedDeviceId
		) {
			setActiveMediaDevice(savedDeviceId);
		}
	}, [devices, kind, deviceSelections, activeDeviceId, setActiveMediaDevice]);

	const activeDevice = devices.find((d) => d.deviceId === activeDeviceId);
	const filteredDevices = devices.filter((d) => d.deviceId !== "default");

	const handleDeviceChange = (deviceId: string) => {
		setActiveMediaDevice(deviceId);
		setDeviceSelection(kind, deviceId);
	};

	return (
		<Select onValueChange={handleDeviceChange} value={activeDeviceId || ""}>
			<SelectTrigger className="h-10 w-[220px] gap-2 rounded-full border-muted-foreground/20 bg-muted/50 hover:bg-muted">
				{icon && <span className="shrink-0 text-muted-foreground">{icon}</span>}
				<SelectValue placeholder={placeholder || "Select device"}>
					<span className="truncate text-sm">
						{activeDevice?.label ||
							`Device ${activeDeviceId?.slice(0, DEVICE_ID_PREVIEW_LENGTH)}`}
					</span>
				</SelectValue>
			</SelectTrigger>
			<SelectContent>
				{filteredDevices.map((device) => (
					<SelectItem key={device.deviceId} value={device.deviceId}>
						{device.label ||
							`Device ${device.deviceId.slice(0, DEVICE_ID_PREVIEW_LENGTH)}`}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

type ControlBarSettingsProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	type: "audio" | "video";
};

export function ControlBarSettings({ type }: ControlBarSettingsProps) {
	return (
		<PopoverContent
			align="center"
			className="w-auto rounded-2xl border-border/50 bg-card p-3 shadow-lg"
			side="top"
			sideOffset={12}
		>
			{type === "audio" ? (
				<div className="flex items-center gap-3">
					<div className="flex-1">
						<CompactMediaDeviceSelect
							icon={<Mic className="size-4" />}
							kind="audioinput"
							placeholder="Select microphone"
						/>
					</div>

					<div className="flex-1">
						<CompactMediaDeviceSelect
							icon={<Volume2 className="size-4" />}
							kind="audiooutput"
							placeholder="Select speaker"
						/>
					</div>

					<Dialog>
						<DialogTrigger asChild>
							<Button
								className="size-10 shrink-0 rounded-full"
								size="icon"
								variant="secondary"
							>
								<Settings className="size-5" />
								<span className="sr-only">Open full settings</span>
							</Button>
						</DialogTrigger>
						<DialogContent className="p-10 sm:max-w-4xl">
							<DialogHeader>
								<DialogTitle>Voice & Video Settings</DialogTitle>
							</DialogHeader>
							<VoiceVideoSettings />
						</DialogContent>
					</Dialog>
				</div>
			) : (
				<div className="flex items-center gap-3">
					<div className="flex-1">
						<CompactMediaDeviceSelect
							icon={<Video className="size-4" />}
							kind="videoinput"
							placeholder="Select camera"
						/>
					</div>

					<Dialog>
						<DialogTrigger asChild>
							<Button
								className="h-10 gap-2 rounded-full px-4"
								variant="secondary"
							>
								Backgrounds and effects
							</Button>
						</DialogTrigger>
						<DialogContent className="p-10 sm:max-w-4xl">
							<DialogHeader>
								<DialogTitle>Voice & Video Settings</DialogTitle>
							</DialogHeader>
							<VoiceVideoSettings initialTab="video" />
						</DialogContent>
					</Dialog>

					<Dialog>
						<DialogTrigger asChild>
							<Button
								className="size-10 shrink-0 rounded-full"
								size="icon"
								variant="secondary"
							>
								<Settings className="size-5" />
								<span className="sr-only">Open full settings</span>
							</Button>
						</DialogTrigger>
						<DialogContent className="p-10 sm:max-w-4xl">
							<DialogHeader>
								<DialogTitle>Voice & Video Settings</DialogTitle>
							</DialogHeader>
							<VoiceVideoSettings initialTab="video" />
						</DialogContent>
					</Dialog>
				</div>
			)}
		</PopoverContent>
	);
}
